import { sortBy } from "lodash-es";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import AdditionalMapping, {
  AdditionalMappingKey,
  SalesforceSourceMapping,
} from "@mapmycustomers/shared/types/integrations/AdditionalMapping";
import { SyncDirections } from "@mapmycustomers/shared/types/integrations/IntegrationRaw";
import {
  EntityTypesSupportedByIntegrations,
  IntegrationFieldsKey,
} from "@mapmycustomers/shared/types/integrations/types";

import Integration from "@app/types/integrations/Integration";

export interface ConfiguredSource {
  entry: AdditionalMapping;
  key: AdditionalMappingKey;
  live: boolean;
}

export interface MappingTarget {
  key: IntegrationFieldsKey;
  parent: EntityTypesSupportedByIntegrations;
}

// The server refuses these as source keys, so an entry hand-edited under one is never a source.
const RESERVED_KEYS: ReadonlySet<string> = new Set([
  "accounts",
  "activities",
  "contacts",
  "crm-activities",
  "crmActivities",
  "deals",
]);

const SOURCE_PARENTS: ReadonlySet<string> = new Set([
  EntityType.COMPANY,
  EntityType.DEAL,
  EntityType.PERSON,
]);

const RECORD_TYPES: ReadonlySet<string> = new Set([
  EntityType.ACTIVITY,
  EntityType.COMPANY,
  EntityType.DEAL,
  EntityType.PERSON,
]);

const isFilled = (value: unknown) => typeof value === "string" && !!value.trim();

const isSalesforceSource = (entry: AdditionalMapping): entry is SalesforceSourceMapping =>
  SOURCE_PARENTS.has(entry.parent);

// Every part of the server's liveness test except the prefix.
const getSourceEntry = (
  integration: Integration | undefined,
  key: string
): SalesforceSourceMapping | undefined => {
  const metadata = integration?.metadata;
  const mappings = metadata?.additionalMappings;
  if (
    integration?.type !== IntegrationService.SALESFORCE ||
    metadata?.multiObjectMappingEnabled !== true ||
    !mappings ||
    RESERVED_KEYS.has(key) ||
    !Object.hasOwn(mappings, key)
  ) {
    return undefined;
  }
  // Metadata is hand-edited JSON, so an entry can be null despite its type.
  const entry: AdditionalMapping | null | undefined = mappings[key as AdditionalMappingKey];
  return entry && isSalesforceSource(entry) && isFilled(entry.name) ? entry : undefined;
};

// Only the Companies/People entry shape declares sourceKeyPrefix, but the server requires it on Deals too.
const hasPrefix = (entry: SalesforceSourceMapping) =>
  "sourceKeyPrefix" in entry && isFilled(entry.sourceKeyPrefix);

// Mirrors the server's liveness test: the server answers 400 for any key that fails it.
const getLiveEntry = (
  integration: Integration | undefined,
  key: string
): SalesforceSourceMapping | undefined => {
  const entry = getSourceEntry(integration, key);
  return entry && hasPrefix(entry) ? entry : undefined;
};

export const isLiveSource = (
  integration: Integration | undefined,
  key: string
): key is AdditionalMappingKey => !!getLiveEntry(integration, key);

export const isSynced = (
  integration: Integration,
  recordType: EntityTypesSupportedByIntegrations
) =>
  !!(
    integration.syncOptions[recordType]?.incoming || integration.syncOptions[recordType]?.outgoing
  );

export const getMappingParent = (
  integration: Integration | undefined,
  key: IntegrationFieldsKey
): EntityTypesSupportedByIntegrations | undefined => {
  if (RECORD_TYPES.has(key)) {
    return key as EntityTypesSupportedByIntegrations;
  }
  return getLiveEntry(integration, key)?.parent;
};

export const getConfiguredSources = (
  integration: Integration | undefined,
  recordType: EntityTypesSupportedByIntegrations
): ConfiguredSource[] => {
  const mappings = integration?.metadata?.additionalMappings;
  if (!mappings) {
    return [];
  }
  const sources = (Object.keys(mappings) as AdditionalMappingKey[])
    .filter((key) => (mappings[key] as AdditionalMapping | null)?.parent === recordType)
    .map((key) => ({ entry: mappings[key], key, live: isLiveSource(integration, key) }));
  // sortBy is stable, so entries without `order` keep their key order after the ordered ones.
  return sortBy(sources, ({ entry }) => entry.order ?? Number.POSITIVE_INFINITY);
};

export const getMappingTargets = (integration: Integration): MappingTarget[] => {
  const synced = (
    Object.keys(integration.syncOptions) as EntityTypesSupportedByIntegrations[]
  ).filter((recordType) => isSynced(integration, recordType));
  const sources = synced.flatMap((parent) =>
    getConfiguredSources(integration, parent)
      .filter(({ live }) => live)
      .map(({ key }) => ({ key, parent }))
  );
  return [...synced.map((parent) => ({ key: parent, parent })), ...sources];
};

export type SourceDisplayState = "hidden" | "live" | "pending";

const getDisplayState = (entry: SalesforceSourceMapping | undefined): SourceDisplayState => {
  if (!entry) {
    return "hidden";
  }
  if (hasPrefix(entry)) {
    return "live";
  }
  // The server fills only an absent prefix, so a blank one never becomes live.
  return "sourceKeyPrefix" in entry && entry.sourceKeyPrefix != null ? "hidden" : "pending";
};

export const getSourceDisplayState = (
  integration: Integration | undefined,
  key: string
): SourceDisplayState => getDisplayState(getSourceEntry(integration, key));

export type SourceDirection = "incoming" | "none" | "outgoing" | "same";

// A source's own flags narrow its parent's direction, as the sync engine applies them.
export const getSourceDirection = (
  entry: SalesforceSourceMapping,
  parent: SyncDirections
): SourceDirection => {
  const incoming = parent.incoming && !entry.outgoingOnly;
  const outgoing = parent.outgoing && !entry.incomingOnly;
  if (incoming === parent.incoming && outgoing === parent.outgoing) {
    return "same";
  }
  if (incoming) {
    return "incoming";
  }
  return outgoing ? "outgoing" : "none";
};

export interface VisibleSource {
  entry: SalesforceSourceMapping;
  key: AdditionalMappingKey;
  state: Exclude<SourceDisplayState, "hidden">;
}

export type VisibleSourcesByParent = Partial<
  Record<EntityTypesSupportedByIntegrations, VisibleSource[]>
>;

const getVisibleSources = (
  integration: Integration | undefined,
  recordType: EntityTypesSupportedByIntegrations
): VisibleSource[] =>
  getConfiguredSources(integration, recordType).flatMap(({ key }) => {
    const entry = getSourceEntry(integration, key);
    const state = getDisplayState(entry);
    return entry && state !== "hidden" ? [{ entry, key, state }] : [];
  });

export const getVisibleSourcesByParent = (
  integration: Integration | undefined
): VisibleSourcesByParent => ({
  [EntityType.COMPANY]: getVisibleSources(integration, EntityType.COMPANY),
  [EntityType.DEAL]: getVisibleSources(integration, EntityType.DEAL),
  [EntityType.PERSON]: getVisibleSources(integration, EntityType.PERSON),
});
